
























































import { Component, Vue, Watch } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import WorkspaceViewModel from '@/workspaces/models/workspaceViewModel';
import { HANDLE_SERIOUS_ERROR } from '@/store/app/actionTypes';
import { contactsApi } from '@/features/contacts/contacts';
import PersonSearchIcon from '@/shared/components/PersonSearchIcon.vue';
import { ContactViewModel } from '@/features/contacts/models/contactViewModel';
import Toast from '@/shared/services/toast';
import ContactTypePicker from '@/features/contacts/components/ContactTypePicker.vue';
import { ContactType } from '@/features/contacts/models/contactType';
import DataTable from '@/shared/components/dataTable/DataTable.vue';
import ContactDisplayModel from '@/features/contacts/models/contactDisplayModel';
import { contactColumns } from '@/features/contacts/models/contactDataTableColumns';
import { ConditionGroupViewModel } from '@/shared/components/conditions/models/conditionGroupViewModel';
import { ConditionViewModel } from '@/shared/components/conditions/models/conditionViewModel';
import { IConditionsManager } from '@/shared/components/conditions/models/conditionsManager';
import { FiltersManager } from './../models/filtersManager';
import ConditionGroupBuilder, { ConditionGroupUpdate } from '@/shared/components/conditions/ConditionGroupBuilder.vue';
import PageData from '@/shared/components/dataTable/pageData';
import FriendlyDateTime from '@/shared/components/FriendlyDateTime.vue';
import FriendlyString from '@/shared/components/FriendlyString.vue';
import { ContactAttributeViewModel } from '@/messaging/models/ContactAttributeViewModel';
import AttributeIconUtil from '../../../shared/util/attributeIconUtil';
import ColumnPicker from '@/shared/components/dataTable/ColumnPicker.vue';
import DataTableColumn from '@/shared/components/dataTable/dataTableColumn';

@Component({
	components: {
		ContactTypePicker,
		PersonSearchIcon,
		DataTable,
		ConditionGroupBuilder,
		FriendlyDateTime,
		FriendlyString,
		ColumnPicker
	}
})
export default class ContactsList extends Vue {
	@Getter public currentWorkspace!: WorkspaceViewModel;
	@Getter public contactAttributes!: ContactAttributeViewModel[];
	@Action(HANDLE_SERIOUS_ERROR) public handleSeriousError: any;

	public visibleColumns: DataTableColumn[] = contactColumns;
	public contacts: ContactViewModel[] = [];
	public formattedContacts: ContactDisplayModel[] = [];

	public pageData: PageData;
	public total: number = 0;
	public rowsPerPage: number = 25;
	public contactType: ContactType = ContactType.Lead;
	public hasInvalidConditions = false;
	public previousFilterString = '';

	public reloading: boolean = false;

	public filtersManager: IConditionsManager = new FiltersManager();

	public filters: ConditionGroupViewModel = {
		type: 'and',
		conditions: []
	};

	@Watch('contactAttributes')
	public handleContactAttributesChange() {
		this.filtersManager = new FiltersManager();
	}

	public async created() {
		if (!this.currentWorkspace) {
			this.handleSeriousError('No workspace selected');
			return;
		}
		await this.loadContacts();
		this.$emit('loaded');
	}

	public async loadContacts() {
		try {
			this.reloading = true;
			const data = await contactsApi.getContacts(
				{
					offset: 0,
					limit: this.rowsPerPage,
					sort: {
						attribute: 'lastSeen',
						direction: 'desc'
					},
					filters: this.createFiltersForRequest()
				},
				this.currentWorkspace.id
			);
			this.total = data.total;
			this.contacts = data.page;
			this.formatData();
		} catch (err) {
			this.handleSeriousError(err.message);
			Toast.showError(`Error loading contacts ${err.message}`);
		} finally {
			this.reloading = false;
		}
	}

	public hasResults() {
		return this.total > 0;
	}

	public handleCellClick(contact: ContactViewModel) {
		this.$router.push({
			name: 'contact-view',
			params: { id: contact.id }
		});
	}

	public getContactAttributesToFormat() {
		// all but name, as we want the default DataTable formatting so that the cell is clickable
		// also making a clone as changing the id of the reference means we'd have to strip the .customAttributes in a couple places
		const contactAttributesToFormat = JSON.parse(JSON.stringify(this.contactAttributes.slice(1)));
		for (const contactAttribute of contactAttributesToFormat) {
			if (!contactAttribute.system) {
				contactAttribute.id = this.getCustomAttributeId(contactAttribute.id);
			}
		}
		return contactAttributesToFormat;
	}

	public isNameEmpty(name: string) {
		return name.length === 0 || !name.trim();
	}

	public getColumns() {
		const allContactColumns: DataTableColumn[] = [];
		allContactColumns.push(...contactColumns);
		allContactColumns.push(...this.getCustomAttributeColumns());
		return allContactColumns;
	}

	public getContactName(name: string | undefined): string {
		if (name && !this.isNameEmpty(name)) {
			return name;
		}
		return 'Unknown';
	}

	public async onFiltersChanged(update: ConditionGroupUpdate) {
		this.filters = update.conditionGroup;
		this.hasInvalidConditions = update.invalid;

		const newFilterString = JSON.stringify(this.filters);
		if (this.previousFilterString !== newFilterString) {
			await this.reloadContacts(true);
			this.previousFilterString = newFilterString;
		}
	}

	public async contactTypeUpdated(newContactType: ContactType) {
		this.contactType = newContactType;
		await this.reloadContacts(true);
	}

	public async dataTableChanged(pageData: PageData) {
		this.pageData = pageData;
		await this.reloadContacts(false);
	}

	private getCustomAttributeColumns(): DataTableColumn[] {
		if (this.contactAttributes) {
			const customAttributes = this.contactAttributes.filter(a => !a.system);
			return customAttributes.map(attribute => {
				return {
					display: attribute.name,
					name: this.getCustomAttributeId(attribute.id),
					sortable: true,
					clickable: false,
					icon: AttributeIconUtil.getIcon(attribute.type, attribute.id, false)
				} as DataTableColumn;
			});
		}
		return [];
	}

	private async reloadContacts(clearOffset: boolean) {
		// don't automatically reload contacts list if there invalid conditions
		if (this.hasInvalidConditions) {
			return;
		}
		try {
			this.reloading = true;
			const data = await contactsApi.getContacts(
				{
					offset: !clearOffset && this.pageData ? this.pageData.offset : 0,
					limit: this.rowsPerPage,
					sort: {
						attribute: this.pageData ? this.pageData.sortBy : 'lastSeen',
						direction: this.pageData ? this.pageData.sortDirection : 'desc'
					},
					filters: this.createFiltersForRequest()
				},
				this.currentWorkspace.id
			);
			this.total = data.total;
			this.contacts = data.page;
			this.formatData();
		} catch (err) {
			this.handleSeriousError(err.message);
			Toast.showError(`Error loading contacts ${err.message}`);
		} finally {
			this.reloading = false;
		}
	}

	private createFiltersForRequest() {
		const typeFilter: ConditionViewModel = {
			attribute: 'type',
			operation: 'equals',
			type: 'string',
			value: this.contactType,
			result: true
		};

		const req: ConditionGroupViewModel = {
			type: 'and',
			conditions: [typeFilter]
		};

		if (this.filters.conditions.length > 0) {
			req.conditions.push(this.filters);
		}

		return req;
	}

	private formatData() {
		const formattedContacts = [];
		for (const contact of this.contacts) {
			const formattedContact = {
				id: contact.id,
				name: this.getContactName(contact.name),
				email: contact.email,
				phoneNumber: contact.phoneNumber,
				lastSeen: contact.lastSeenUtc,
				lastContacted: contact.lastContactedUtc,
				lastHeardFrom: contact.lastHeardFromUtc,
				firstSeen: contact.firstSeenUtc
			};
			this.addCustomAttributeValues(formattedContact, contact);

			formattedContacts.push(formattedContact);
		}
		this.formattedContacts = formattedContacts;
	}

	private addCustomAttributeValues(formattedContact: any, contact: ContactViewModel) {
		const customAttributes = this.contactAttributes.filter(a => !a.system);
		for (const attribute of customAttributes) {
			formattedContact[this.getCustomAttributeId(attribute.id)] = contact.customAttributes[attribute.id];
		}
	}

	private getCustomAttributeId(id: string) {
		return `customAttributes.${id}`;
	}
}
